import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { createBooking } from "@/lib/bookings";
import { resendVerification } from "@/lib/auth-flows";
import { validateCoupon, type ValidateCouponResult } from "@/lib/coupons";
import { getTourBySlug, type TourDetail } from "@/lib/tours";
import { useAuth } from "@/contexts/AuthContext";
import AvailabilityPicker from "./components/AvailabilityPicker";
import { type PublicAvailabilitySlot } from "@/lib/availability";
import { onTourImageError } from "@/lib/imageFallback";

export default function BookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation("booking");
  const { user } = useAuth();
  const priceLocale = i18n.language === "es" ? "es-MX" : "en-US";

  const [tour, setTour] = useState<TourDetail | null>(null);
  const [loadingTour, setLoadingTour] = useState(true);
  const [tourError, setTourError] = useState<string | null>(null);

  const [step, setStep] = useState(1);
  const todayPlusTwo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 10);
  }, []);
  const [date, setDate] = useState(todayPlusTwo);
  const [time, setTime] = useState("09:00");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [requests, setRequests] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleResendVerification = async () => {
    setResendState("sending");
    try {
      await resendVerification();
      setResendState("sent");
    } catch {
      setResendState("error");
    }
  };

  // Availability: if the provider has configured per-date capacity, use the
  // visual picker; otherwise fall back to the plain date input.
  const [usePicker, setUsePicker] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState<PublicAvailabilitySlot | null>(null);

  // Coupons
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState<ValidateCouponResult | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    if (user) {
      const parts = user.fullName.trim().split(/\s+/);
      if (parts[0]) setFirstName(parts[0]);
      if (parts.length > 1) setLastName(parts.slice(1).join(" "));
      setEmail(user.email);
      if (user.phone) setPhone(user.phone);
    }
  }, [user]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoadingTour(true);
    setTourError(null);
    getTourBySlug(slug)
      .then((d) => { if (!cancelled) setTour(d); })
      .catch((err) => {
        if (cancelled) return;
        setTourError(err instanceof ApiError && err.status === 404 ? t("booking.notFound") : t("booking.loadError"));
      })
      .finally(() => { if (!cancelled) setLoadingTour(false); });
    return () => { cancelled = true; };
  }, [slug, t]);

  const effectivePriceAdult = selectedSlot?.priceOverride ?? tour?.priceAdult ?? 0;
  const adultsTotal = tour ? adults * effectivePriceAdult : 0;
  const childrenTotal = tour ? children * (tour.priceChild ?? 0) : 0;
  const subtotal = adultsTotal + childrenTotal;
  const discount = coupon?.valid ? coupon.appliedDiscount : 0;
  const total = Math.max(0, subtotal - discount);

  // Re-validate the coupon when the subtotal changes (e.g. guest count edited
  // after applying) so we never send MP an outdated discount.
  useEffect(() => {
    if (!coupon?.valid) return;
    let cancelled = false;
    validateCoupon(coupon.code, subtotal)
      .then((r) => { if (!cancelled) setCoupon(r); })
      .catch(() => { /* silent — checkout submit re-validates on the server side */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const handleApplyCoupon = async () => {
    const trimmed = couponCode.trim();
    if (!trimmed) return;
    setCouponError(null);
    setValidatingCoupon(true);
    try {
      const res = await validateCoupon(trimmed, subtotal);
      if (res.valid) {
        setCoupon(res);
      } else {
        setCoupon(null);
        setCouponError(t(`booking.couponReason.${res.reason ?? "INVALID"}`, {
          defaultValue: t("booking.couponInvalid", { defaultValue: "Cupón no válido." }),
        }));
      }
    } catch (err) {
      setCoupon(null);
      setCouponError(t("booking.couponError", { defaultValue: "No pudimos validar el cupón." }));
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCoupon(null);
    setCouponCode("");
    setCouponError(null);
  };

  const validateStep1 = () => {
    const e: Record<string, string> = {};
    if (!date) e.date = t("booking.required");
    if (adults + children === 0) e.guests = t("booking.required");
    if (tour && adults + children > tour.maxGuests)
      e.guests = t("booking.maxGuests", { count: tour.maxGuests });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e: Record<string, string> = {};
    if (!firstName.trim()) e.firstName = t("booking.required");
    if (!lastName.trim()) e.lastName = t("booking.required");
    if (!email.trim()) e.email = t("booking.required");
    else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = t("booking.invalidEmail");
    if (!phone.trim()) e.phone = t("booking.required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleContinue = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleConfirm = async () => {
    if (!tour) return;
    setApiError(null);
    setProcessing(true);
    try {
      const res = await createBooking({
        tourId: tour.id,
        date,
        startTime: time ? `${time}:00` : undefined,
        adults,
        children,
        contactName: `${firstName.trim()} ${lastName.trim()}`.trim(),
        contactEmail: email.trim(),
        contactPhone: phone.trim() || undefined,
        notes: requests.trim() || undefined,
        couponCode: coupon?.valid ? coupon.code : undefined,
      });
      window.location.href = res.initPoint;
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message || t("booking.createError"));
      } else {
        setApiError(t("booking.createError"));
      }
      setProcessing(false);
    }
  };

  if (loadingTour) {
    return (
      <div className="min-h-screen bg-offwhite pt-20 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-ocean border-t-transparent animate-spin" />
      </div>
    );
  }

  if (tourError || !tour) {
    return (
      <div className="min-h-screen bg-offwhite pt-20 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
            <i className="ri-map-pin-line text-4xl" />
          </div>
          <h1 className="text-xl font-bold text-charcoal mb-2">{tourError ?? t("booking.notFound")}</h1>
          <Link to="/tours" className="inline-flex bg-ocean text-white text-sm font-medium px-6 py-2.5 rounded-full">
            {t("booking.backToTours")}
          </Link>
        </div>
      </div>
    );
  }

  const coverImage = tour.images[0]?.url;
  const childPriceShown = tour.priceChild ?? 0;
  const childAllowed = tour.priceChild !== null;

  const steps = [
    { num: 1, label: t("booking.step1") },
    { num: 2, label: t("booking.step2") },
    { num: 3, label: t("booking.step3") },
  ];

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20 pb-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 pt-4">
            <h1 className="text-2xl md:text-3xl font-bold text-charcoal mb-1">{t("booking.title")}</h1>
            <p className="text-sm text-gray-500">{t("booking.subtitle")}</p>
          </div>

          {/* The API refuses bookings from unconfirmed addresses, so say it up front
              instead of letting the customer fill the whole form and fail at the end. */}
          {user && !user.emailVerified && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 md:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                <i className="ri-mail-check-line" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-charcoal">{t("booking.verifyTitle")}</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  {resendState === "sent" ? t("booking.verifySent") : resendState === "error" ? t("booking.verifyResendError") : t("booking.verifyDesc")}
                </p>
              </div>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendState === "sending" || resendState === "sent"}
                className="shrink-0 border border-amber-300 text-amber-700 text-xs font-medium px-4 py-2 rounded-full hover:bg-amber-100 transition-colors disabled:opacity-50"
              >
                {resendState === "sending" ? t("booking.verifySending") : t("booking.verifyResend")}
              </button>
            </div>
          )}

          <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-center gap-2 md:gap-4">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-bold transition-colors ${
                      step >= s.num ? "bg-ocean text-white" : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {step > s.num ? <i className="ri-check-line" /> : s.num}
                  </div>
                  <span className={`text-xs mt-1.5 font-medium hidden md:block ${step >= s.num ? "text-ocean" : "text-gray-400"}`}>
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-8 md:w-16 h-px transition-colors ${step > s.num ? "bg-ocean" : "bg-gray-200"}`} />
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <div className="flex-1 min-w-0">
              {step === 1 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 space-y-5">
                  <h2 className="text-lg font-bold text-charcoal">{t("booking.tourSummary")}</h2>

                  <div className="flex gap-4">
                    {coverImage && <img src={coverImage} alt={tour.title} onError={onTourImageError} className="w-24 h-20 object-cover rounded-xl shrink-0" />}
                    <div>
                      <h3 className="text-base font-semibold text-charcoal">{tour.title}</h3>
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-xs" /></div>
                        {tour.location}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-xs" /></div>
                        {tour.duration}
                      </div>
                    </div>
                  </div>

                  {usePicker && tour ? (
                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1.5">{t("booking.date")}</label>
                      <AvailabilityPicker
                        slug={tour.slug}
                        guests={Math.max(1, adults + children)}
                        value={date}
                        locale={priceLocale}
                        onChange={(d, slot) => {
                          setDate(d);
                          setSelectedSlot(slot);
                          if (slot?.startTime) setTime(slot.startTime.slice(0, 5));
                        }}
                        onEmpty={() => setUsePicker(false)}
                      />
                      {errors.date && <p className="text-xs text-coral mt-1">{errors.date}</p>}
                      {selectedSlot && (
                        <p className="text-xs text-gray-500 mt-2">
                          {date} {selectedSlot.startTime ? `· ${selectedSlot.startTime.slice(0, 5)}` : ""}
                          {" — "}
                          {t("booking.spotsAvailable", { count: selectedSlot.remaining })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-charcoal mb-1.5">{t("booking.date")}</label>
                        <input
                          type="date"
                          value={date}
                          min={new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setDate(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
                        />
                        {errors.date && <p className="text-xs text-coral mt-1">{errors.date}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-charcoal mb-1.5">{t("booking.time")}</label>
                        <input
                          type="time"
                          value={time}
                          onChange={(e) => setTime(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-3">{t("booking.guests")}</label>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1 flex items-center justify-between border border-gray-200 rounded-lg px-4 py-2.5">
                        <div>
                          <p className="text-sm font-medium text-charcoal">{t("booking.adults")}</p>
                          <p className="text-xs text-gray-400">${tour.priceAdult.toLocaleString(priceLocale)} {t("booking.perPerson")}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => setAdults(Math.max(0, adults - 1))} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                            <i className="ri-subtract-line" />
                          </button>
                          <span className="text-sm font-semibold w-4 text-center">{adults}</span>
                          <button type="button" onClick={() => setAdults(Math.min(tour.maxGuests, adults + 1))} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                            <i className="ri-add-line" />
                          </button>
                        </div>
                      </div>
                      {childAllowed && (
                        <div className="flex-1 flex items-center justify-between border border-gray-200 rounded-lg px-4 py-2.5">
                          <div>
                            <p className="text-sm font-medium text-charcoal">{t("booking.children")}</p>
                            <p className="text-xs text-gray-400">${childPriceShown.toLocaleString(priceLocale)} {t("booking.perChild")}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <button type="button" onClick={() => setChildren(Math.max(0, children - 1))} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                              <i className="ri-subtract-line" />
                            </button>
                            <span className="text-sm font-semibold w-4 text-center">{children}</span>
                            <button type="button" onClick={() => setChildren(Math.min(tour.maxGuests - adults, children + 1))} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                              <i className="ri-add-line" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.guests && <p className="text-xs text-coral mt-1">{errors.guests}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1.5">{t("booking.specialRequests")}</label>
                    <textarea
                      value={requests}
                      onChange={(e) => setRequests(e.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder={t("booking.specialRequestsPlaceholder")}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean resize-none"
                    />
                  </div>

                  <button
                    onClick={handleContinue}
                    className="w-full bg-ocean hover:bg-ocean/90 text-white font-medium py-3 rounded-full transition-colors"
                  >
                    {t("booking.continue")}
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 space-y-5">
                  <h2 className="text-lg font-bold text-charcoal">{t("booking.contactInfo")}</h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1.5">{t("booking.firstName")}</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
                      />
                      {errors.firstName && <p className="text-xs text-coral mt-1">{errors.firstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1.5">{t("booking.lastName")}</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
                      />
                      {errors.lastName && <p className="text-xs text-coral mt-1">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1.5">{t("booking.email")}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
                    />
                    {errors.email && <p className="text-xs text-coral mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1.5">{t("booking.phone")}</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
                      placeholder="+52"
                    />
                    {errors.phone && <p className="text-xs text-coral mt-1">{errors.phone}</p>}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(1)}
                      className="flex-1 border border-gray-200 text-charcoal font-medium py-3 rounded-full hover:bg-gray-50 transition-colors"
                    >
                      {t("booking.back")}
                    </button>
                    <button
                      onClick={handleContinue}
                      className="flex-1 bg-ocean hover:bg-ocean/90 text-white font-medium py-3 rounded-full transition-colors"
                    >
                      {t("booking.continue")}
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 space-y-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-ocean/10 text-ocean">
                      <i className="ri-secure-payment-line text-lg" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-charcoal">{t("booking.mpTitle")}</h2>
                      <p className="text-xs text-gray-500">{t("booking.mpDesc")}</p>
                    </div>
                  </div>

                  {apiError && (
                    <div className="rounded-lg border border-coral/30 bg-coral/10 px-3 py-2 text-sm text-coral">
                      {apiError}
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">{t("booking.mpAccepted")}</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex flex-col items-center gap-2 p-3 border border-gray-100 rounded-xl bg-gray-50">
                        <div className="w-8 h-8 flex items-center justify-center text-ocean">
                          <i className="ri-bank-card-line text-xl" />
                        </div>
                        <span className="text-xs text-center text-gray-600 leading-tight">{t("booking.mpCard")}</span>
                      </div>
                      <div className="flex flex-col items-center gap-2 p-3 border border-gray-100 rounded-xl bg-gray-50">
                        <div className="w-8 h-8 flex items-center justify-center text-coral">
                          <i className="ri-store-line text-xl" />
                        </div>
                        <span className="text-xs text-center text-gray-600 leading-tight">{t("booking.mpOxxo")}</span>
                      </div>
                      <div className="flex flex-col items-center gap-2 p-3 border border-gray-100 rounded-xl bg-gray-50">
                        <div className="w-8 h-8 flex items-center justify-center text-turquoise">
                          <i className="ri-exchange-dollar-line text-xl" />
                        </div>
                        <span className="text-xs text-center text-gray-600 leading-tight">{t("booking.mpSpei")}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      {t("booking.couponLabel", { defaultValue: "Código de descuento" })}
                    </label>
                    {coupon?.valid ? (
                      <div className="flex items-center justify-between gap-3 p-3 border border-ocean/30 bg-ocean/5 rounded-xl">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ocean truncate">{coupon.code}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {coupon.description ?? t("booking.couponApplied", { defaultValue: "Cupón aplicado" })}
                            {" — "}
                            -${coupon.appliedDiscount.toLocaleString(priceLocale)} MXN
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveCoupon}
                          className="shrink-0 text-xs font-medium text-coral hover:underline"
                        >
                          {t("booking.couponRemove", { defaultValue: "Quitar" })}
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }}
                          placeholder={t("booking.couponPlaceholder", { defaultValue: "Ej. BAJA10" })}
                          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-ocean uppercase"
                          maxLength={32}
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={!couponCode.trim() || validatingCoupon}
                          className="px-4 py-2 text-sm font-medium border border-ocean text-ocean rounded-lg hover:bg-ocean/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {validatingCoupon ? "…" : t("booking.couponApply", { defaultValue: "Aplicar" })}
                        </button>
                      </div>
                    )}
                    {couponError && (
                      <p className="text-xs text-coral mt-1.5">{couponError}</p>
                    )}
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{tour.title}</span>
                      <span className="font-medium text-charcoal">{date}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{adults + children} {t("booking.guests")}</span>
                      <span className="font-medium text-charcoal">{time}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t("booking.discount", { defaultValue: "Descuento" })} ({coupon?.code})</span>
                        <span className="font-medium text-coral">−${discount.toLocaleString(priceLocale)}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-gray-200 flex justify-between">
                      <span className="font-bold text-charcoal">{t("booking.total")}</span>
                      <span className="font-bold text-ocean">${total.toLocaleString(priceLocale)} MXN</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-xs text-gray-500">
                    <div className="w-4 h-4 flex items-center justify-center text-ocean shrink-0 mt-0.5">
                      <i className="ri-shield-check-line" />
                    </div>
                    {t("booking.mpSecure")}
                  </div>

                  <p className="text-xs text-gray-500">
                    {t("booking.termsNote")}{" "}
                    <Link to="/" className="text-ocean hover:underline">{t("booking.termsLink")}</Link>.
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setStep(2)}
                      disabled={processing}
                      className="flex-1 border border-gray-200 text-charcoal font-medium py-3 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {t("booking.back")}
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={processing}
                      className="flex-1 bg-coral hover:bg-coral/90 text-white font-medium py-3 rounded-full transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
                    >
                      {processing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          {t("booking.processing")}
                        </>
                      ) : (
                        <>
                          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-right-line" /></div>
                          {t("booking.mpPayButton")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:w-80 shrink-0">
              <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
                <h3 className="text-base font-bold text-charcoal mb-4">{t("booking.priceSummary")}</h3>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t("booking.adultsTotal")} ({adults})</span>
                    <span className="font-medium">${adultsTotal.toLocaleString(priceLocale)}</span>
                  </div>
                  {children > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t("booking.childrenTotal")} ({children})</span>
                      <span className="font-medium">${childrenTotal.toLocaleString(priceLocale)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t("booking.subtotal")}</span>
                    <span className="font-medium">${subtotal.toLocaleString(priceLocale)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-coral">{t("booking.discount", { defaultValue: "Descuento" })} ({coupon?.code})</span>
                      <span className="font-medium text-coral">−${discount.toLocaleString(priceLocale)}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-gray-100 flex justify-between">
                    <span className="font-bold text-charcoal">{t("booking.total")}</span>
                    <span className="font-bold text-ocean text-lg">${total.toLocaleString(priceLocale)} MXN</span>
                  </div>
                </div>

                <div className="mt-5 space-y-2.5">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-4 h-4 flex items-center justify-center text-ocean"><i className="ri-checkbox-circle-line" /></div>
                    {t("booking.instantConfirm")}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-4 h-4 flex items-center justify-center text-ocean"><i className="ri-checkbox-circle-line" /></div>
                    {t("booking.freeCancel")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
